import axios from 'axios';
import { fmtErr } from '../lib/errors';

// ── Backend URL resolution ───────────────────────────────────────────────
// Historically this was simply `process.env.REACT_APP_BACKEND_URL`, but that
// env var is baked into the JS bundle at build time. When a production
// build is taken with a stale preview-pod URL still in the env, every
// outbound API call from the user's browser fires at a paused host —
// causing whole-app "Network error" toasts even though the real backend
// (e.g. nirman-workhub.com) is healthy.
//
// To make the bundle environment-aware at RUNTIME, we ignore the build-time
// value whenever the page itself is being served from a non-preview origin
// (typical of the production custom-domain deploy). In that case we trust
// `window.location.origin` because the app is served same-origin with the
// API anyway. Local dev and the preview-pod environment continue to honour
// the build-time env var.
const _PREVIEW_HOST_MARKERS = ['.preview.emergentagent.com', 'preview.emergentagent.com'];
const _BUILD_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function _resolveBackendUrl() {
  const pageOrigin = (typeof window !== 'undefined' && window.location?.origin) || '';
  if (!pageOrigin) return _BUILD_BACKEND_URL;
  const pageIsPreview = _PREVIEW_HOST_MARKERS.some(m => pageOrigin.includes(m));
  const pageIsLocalDev = /^https?:\/\/(localhost|127\.0\.0\.1)(:|$)/.test(pageOrigin);
  // Preview or local-dev page → honor build-time env (points at pod / localhost:8001).
  if (pageIsPreview || pageIsLocalDev) return _BUILD_BACKEND_URL || pageOrigin;
  // Any other origin (production custom-domain deploy — e.g. www.nirman-workhub.com
  // vs. nirman-workhub.com) → ALWAYS use same-origin. The backend is proxied at
  // the same host, so this bypasses CORS entirely and dodges the classic
  // "www vs. apex" mismatch where the build was baked with the non-www URL.
  return pageOrigin;
}

const BACKEND_URL = _resolveBackendUrl();
const API = `${BACKEND_URL}/api`;

// Re-export the runtime-resolved values so future modules can
// `import { BACKEND_URL, API } from '../utils/api'` and automatically
// benefit from the same stale-URL hardening.
export { BACKEND_URL, API };

// ── One-time environment health check ────────────────────────────────────
// If we had to override a stale build-time backend URL, log a single
// loud warning so the developer sees it immediately on app load (Network
// tab won't show anything wrong, since the rewrite is silent).
// End users never see this — it only goes to the browser console.
(() => {
  try {
    if (typeof window === 'undefined' || typeof console === 'undefined') return;
    const built = _BUILD_BACKEND_URL || '';
    const resolved = BACKEND_URL || '';
    if (built && resolved && built !== resolved) {
      console.warn(
        '%c[env] Stale REACT_APP_BACKEND_URL detected and corrected at runtime.',
        'color:#f59e0b;font-weight:bold;',
        '\n  Built with: ' + built +
        '\n  Resolved to: ' + resolved +
        '\n  Action: rebuild & redeploy with REACT_APP_BACKEND_URL=' + resolved + ' to remove this warning.'
      );
    }
  // SAFETY: This top-level startup probe MUST NOT throw or block boot —
  // it logs a console warning on stale-URL detection and exits. The empty
  // catch is intentional.
  } catch { /* never block app boot */ }
})();

// Public helper for non-fetch consumers (image `src`, anchor `href`, etc.)
// that need to dodge a stale build-time backend URL the same way the fetch /
// axios interceptors do. Pass an absolute or relative URL; get back the same
// URL but rewritten to same-origin if the absolute form points at a paused
// preview pod and we're currently on a non-preview origin.
const _PREVIEW_HOST_MARKERS_PUB = _PREVIEW_HOST_MARKERS;
function _shouldRewriteToPageOrigin(urlHost, pageOrigin) {
  if (!pageOrigin) return false;
  const pageIsPreview = _PREVIEW_HOST_MARKERS_PUB.some(m => pageOrigin.includes(m));
  const pageIsLocalDev = /^https?:\/\/(localhost|127\.0\.0\.1)(:|$)/.test(pageOrigin);
  // Preview / local-dev pages: only rewrite when the URL host is itself a stale
  // preview marker (existing behaviour). Leaves cross-service calls alone.
  if (pageIsPreview || pageIsLocalDev) {
    return _PREVIEW_HOST_MARKERS_PUB.some(m => urlHost.includes(m));
  }
  // Production custom-domain page: rewrite ANY absolute URL whose host differs
  // from the page host. Covers stale preview URLs AND the classic
  // "bundle built with nirman-workhub.com but user visits www.nirman-workhub.com"
  // (or vice-versa) mismatch that manifests as CORS-blocked requests.
  try {
    const pageHost = new URL(pageOrigin).host;
    return urlHost !== pageHost;
  } catch {
    return false;
  }
}

export function safeUrl(url) {
  if (typeof url !== 'string' || !url) return url;
  // Relative paths are always same-origin already.
  if (!/^https?:\/\//i.test(url)) return url;
  try {
    const u = new URL(url);
    const pageOrigin = (typeof window !== 'undefined' && window.location?.origin) || '';
    if (_shouldRewriteToPageOrigin(u.host, pageOrigin)) {
      return pageOrigin + u.pathname + u.search + u.hash;
    }
    return url;
  } catch {
    return url;
  }
}

// Configure axios to always send cookies (HTTP-only auth cookie)
axios.defaults.withCredentials = true;

// Same defensive URL rewrite as the global fetch override below — when the
// frontend bundle was built with a stale preview backend URL, axios calls
// to that URL would fail too. This interceptor catches absolute URLs that
// still point to a paused preview pod and routes them through the same
// origin instead. (Logic mirrored intentionally; both fetch and axios
// must be hardened or the safety net has holes.)
const _PREVIEW_HOST_MARKERS_AX = _PREVIEW_HOST_MARKERS;
axios.interceptors.request.use((config) => {
  if (typeof config.url === 'string' && /^https?:\/\//i.test(config.url)) {
    try {
      const u = new URL(config.url);
      const pageOrigin = (typeof window !== 'undefined' && window.location?.origin) || '';
      if (_shouldRewriteToPageOrigin(u.host, pageOrigin)) {
        config.url = pageOrigin + u.pathname + u.search + u.hash;
      }
    } catch {
      // Malformed `config.url` (e.g. a relative path that slipped past the
      // regex check) just falls through unchanged. Intentional no-op.
    }
  }
  return config;
});

// ── In-memory JWT store (replaces sessionStorage as of Feb 2026) ─────────
// JWT is now held ONLY in module-scope variables inside this file. XSS
// payloads running in the same JS context can no longer scrape the token
// from sessionStorage. The HttpOnly auth cookie remains the source of
// truth — `rehydrateToken()` mints a fresh in-memory copy on app boot
// using the cookie, and `setToken()` populates it after a successful
// login. On hard reload the in-memory copy is gone; `App.js` calls
// `rehydrateToken()` before rendering so that all Bearer-fallback and
// XHR-upload call sites see a populated token immediately.
let _inMemoryToken = null;
let _isAuthenticated = false;

// One-time migration: if a legacy session still has the JWT in
// sessionStorage from a pre-Phase-B build, lift it into memory and
// erase the persistent copy so this tab no longer leaks it via XSS.
try {
  const _legacy = sessionStorage.getItem('auth_jwt');
  if (_legacy) {
    _inMemoryToken = _legacy;
    _isAuthenticated = true;
    sessionStorage.removeItem('auth_jwt');
  }
} catch { /* sessionStorage may be unavailable in some sandboxed contexts */ }

// Axios interceptor: attach Bearer token to every request as fallback
axios.interceptors.request.use((config) => {
  if (_inMemoryToken && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${_inMemoryToken}`;
  }
  // CRITICAL: When the body is FormData the browser must set Content-Type itself
  // so it can include the multipart boundary. Any manual 'multipart/form-data'
  // header (without boundary) makes proxies/CDNs reject the request.
  if (config.data instanceof FormData) {
    if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
      if (config.headers.common) delete config.headers.common['Content-Type'];
      if (config.headers.post) delete config.headers.post['Content-Type'];
    }
  }
  return config;
});

// Axios response interceptor: mirror the global fetch wrapper below so that
// upload endpoints called via axios (uploadMemberDocument, uploadLegalDocument,
// etc.) also trigger the `StorageCapBanner`. Without this the banner never
// showed for society-member document uploads and users only saw the raw
// per-file toast (reported Jul 2026 — "Nandu is getting StorageCapExceeded").
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const body = error?.response?.data;
    if (status === 507 && body && body.error_code === 'STORAGE_CAP_EXCEEDED') {
      window.dispatchEvent(
        new CustomEvent('storage:cap-exceeded', { detail: fmtErr(body, '') }),
      );
    }
    return Promise.reject(error);
  },
);

// Override global fetch to always include credentials AND Bearer token
// IMPORTANT: Keep headers as plain objects (not Headers instances) so the browser
// auto-sets Content-Type for FormData bodies (multipart/form-data with boundary).
const _originalFetch = window.fetch;

// ── Defensive URL rewrite ────────────────────────────────────────────────
// REACT_APP_BACKEND_URL is baked into the JS bundle at build time. If the
// production frontend was last built with a now-paused preview URL (e.g.
// `https://...preview.emergentagent.com`), every `fetch(${API}/...)` call
// in the codebase fires at a dead host and the browser throws a generic
// "TypeError: Load failed" / "Network error" — even though the API at the
// real domain (e.g. `nirman-workhub.com`) is healthy.
//
// To make all internal fetches resilient to this build-time misconfig, we
// rewrite stale-preview backend URLs to the page's own origin at fetch
// time. This is safe because:
//   • Cookies / Bearer tokens are still attached (we don't change those).
//   • Same-origin requests skip CORS preflight altogether.
//   • Public viewer routes (e.g. PublicAlbumViewer) already use this
//     pattern explicitly; this brings the rest of the app in line.
function _rewriteToSameOriginIfStale(input) {
  // Only do anything for absolute string URLs.
  if (typeof input !== 'string') return input;
  if (!/^https?:\/\//i.test(input)) return input;
  try {
    const u = new URL(input);
    const pageOrigin = (typeof window !== 'undefined' && window.location?.origin) || '';
    if (_shouldRewriteToPageOrigin(u.host, pageOrigin)) {
      return pageOrigin + u.pathname + u.search + u.hash;
    }
    return input;
  } catch {
    return input;
  }
}

window.fetch = function(url, options = {}) {
  const rewrittenUrl = _rewriteToSameOriginIfStale(url);
  const token = _inMemoryToken;
  const originalHeaders = options.headers;
  let finalHeaders;

  if (originalHeaders instanceof Headers) {
    // Already a Headers object — mutate in place
    if (token && !originalHeaders.has('Authorization')) {
      originalHeaders.set('Authorization', `Bearer ${token}`);
    }
    // If body is FormData, remove Content-Type so browser sets multipart boundary
    if (options.body instanceof FormData) {
      originalHeaders.delete('Content-Type');
    }
    finalHeaders = originalHeaders;
  } else {
    // Plain object — keep as plain object so browser auto-sets Content-Type for FormData
    finalHeaders = { ...(originalHeaders || {}) };
    if (token && !finalHeaders['Authorization']) {
      finalHeaders['Authorization'] = `Bearer ${token}`;
    }
    // If body is FormData, remove Content-Type so browser sets multipart boundary
    if (options.body instanceof FormData) {
      delete finalHeaders['Content-Type'];
      delete finalHeaders['content-type'];
    }
  }

  return _originalFetch(rewrittenUrl, {
    ...options,
    headers: finalHeaders,
    credentials: 'include',
  }).then((response) => {
    // Global watch for B2 storage-cap exceeded. Backend returns HTTP 507
    // with `error_code: STORAGE_CAP_EXCEEDED` from any upload endpoint.
    // Dispatch a window event so a top-of-page banner (StorageCapBanner)
    // can show without every upload site having to handle it individually.
    if (response.status === 507) {
      response
        .clone()
        .json()
        .then((body) => {
          if (body && body.error_code === 'STORAGE_CAP_EXCEEDED') {
            window.dispatchEvent(
              new CustomEvent('storage:cap-exceeded', { detail: fmtErr(body, '') })
            );
          }
        })
        .catch(() => {
          // Non-JSON 507 body — still surface a generic banner.
          window.dispatchEvent(new CustomEvent('storage:cap-exceeded', { detail: '' }));
        });
    }
    return response;
  });
};

class ApiClient {
  constructor() {
    this.token = _inMemoryToken;
    this.authenticated = _isAuthenticated;
  }

  setToken(token) {
    this.token = token;
    this.authenticated = true;
    _inMemoryToken = token || null;
    _isAuthenticated = !!token;
  }

  clearToken() {
    this.token = null;
    this.authenticated = false;
    _inMemoryToken = null;
    _isAuthenticated = false;
    // Clean up any legacy persistent storage (safe no-op if already gone).
    try { sessionStorage.removeItem('auth_jwt'); } catch { /* ignore */ }
    try { localStorage.removeItem('token'); } catch { /* ignore */ }
  }

  getHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    return headers;
  }

  async login(email, password) {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // Store JWT for Bearer header fallback (in case cookies are blocked by proxy)
    if (response.data?.token) this.setToken(response.data.token);
    return response.data;
  }

  async register(employeeData) {
    const response = await axios.post(`${API}/auth/register`, employeeData);
    return response.data;
  }

  async logout() {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: this.getHeaders() });
    } finally {
      this.clearToken();
    }
  }

  async getCurrentUser() {
    const response = await axios.get(`${API}/auth/me`, { headers: this.getHeaders() });
    return response.data;
  }

  async getAccessProfile() {
    const response = await axios.get(`${API}/auth/access-profile`, { headers: this.getHeaders() });
    return response.data;
  }

  async getEmployees(opts = {}) {
    // Backend now excludes past_employee by default. HR pages that need the
    // full roster (including offboarded staff) should call `getEmployees({ includePast: true })`.
    const params = opts.includePast ? '?include_past=true' : '';
    const response = await axios.get(`${API}/employees${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  // Transcribe a browser-recorded audio Blob via the backend Whisper endpoint.
  // Used by the LetterGenerator's voice-input button.
  async transcribeAudio(blob, ext = 'webm', language = null) {
    const form = new FormData();
    form.append('audio', blob, `recording.${ext}`);
    if (language) form.append('language', language);
    // Pull the Bearer header off `getHeaders()` but DROP the JSON Content-Type
    // so the browser can set its own multipart boundary header.
    const { 'Content-Type': _ignored, ...headers } = this.getHeaders();
    const response = await axios.post(`${API}/voice/transcribe`, form, { headers });
    return response.data;
  }

  async getProjects() {
    const response = await axios.get(`${API}/projects`, { headers: this.getHeaders() });
    return response.data;
  }

  async createProject(projectData) {
    const response = await axios.post(`${API}/projects`, projectData, { headers: this.getHeaders() });
    return response.data;
  }

  async getProject(projectId) {
    const response = await axios.get(`${API}/projects/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteProject(projectId) {
    const response = await axios.delete(`${API}/projects/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async updateProject(projectId, data) {
    const response = await axios.put(`${API}/projects/${projectId}`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async getTasks(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await axios.get(`${API}/tasks?${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getMyTasks() {
    const response = await axios.get(`${API}/tasks/my-tasks`, { headers: this.getHeaders() });
    return response.data;
  }

  async createTask(taskData) {
    const response = await axios.post(`${API}/tasks`, taskData, { headers: this.getHeaders() });
    return response.data;
  }

  async updateTask(taskId, updates) {
    const response = await axios.put(`${API}/tasks/${taskId}`, updates, { headers: this.getHeaders() });
    return response.data;
  }

  async getDashboardStats() {
    const response = await axios.get(`${API}/command-centre/dashboard/stats`, { headers: this.getHeaders() });
    return response.data;
  }

  async uploadDocument(projectId, documentType, file) {
    const formData = new FormData();
    formData.append('project_id', projectId);
    formData.append('document_type', documentType);
    formData.append('file', file);

    const response = await axios.post(`${API}/documents`, formData);
    return response.data;
  }

  async getDocuments(projectId = null) {
    const query = projectId ? `?project_id=${projectId}` : '';
    const response = await axios.get(`${API}/documents${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteDocument(documentId) {
    const response = await axios.delete(`${API}/documents/${documentId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async downloadDocument(documentId) {
    const response = await axios.get(`${API}/documents/${documentId}/download`, {
      headers: this.getHeaders(),
      responseType: 'blob'
    });
    return response.data;
  }

  async getDocumentStats() {
    const response = await axios.get(`${API}/documents/stats`, { headers: this.getHeaders() });
    return response.data;
  }

  // Member Documents
  async uploadMemberDocument(memberId, documentType, file, onProgress = null) {
    const formData = new FormData();
    formData.append('member_id', memberId);
    formData.append('document_type', documentType);
    formData.append('file', file);

    const config = {};
    if (onProgress) {
      config.onUploadProgress = (e) => {
        if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    const response = await axios.post(`${API}/member-documents`, formData, config);
    return response.data;
  }

  async getMemberDocuments(memberId) {
    const response = await axios.get(`${API}/member-documents/${memberId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteMemberDocument(documentId) {
    const response = await axios.delete(`${API}/member-documents/${documentId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async downloadMemberDocument(documentId) {
    const response = await axios.get(`${API}/member-documents/${documentId}/download`, {
      headers: this.getHeaders(),
      responseType: 'blob'
    });
    return response.data;
  }

  // Legal Documents
  async uploadLegalDocument(projectId, documentType, file) {
    const formData = new FormData();
    formData.append('project_id', projectId);
    formData.append('document_type', documentType);
    formData.append('file', file);

    const response = await axios.post(`${API}/legal-documents`, formData);
    return response.data;
  }

  async getLegalDocuments(projectId) {
    const response = await axios.get(`${API}/legal-documents/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteLegalDocument(documentId) {
    const response = await axios.delete(`${API}/legal-documents/${documentId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async downloadLegalDocument(documentId) {
    const response = await axios.get(`${API}/legal-documents/${documentId}/download`, {
      headers: this.getHeaders(),
      responseType: 'blob'
    });
    return response.data;
  }

  async compareDocuments(docId1, docId2, comparisonType = 'detailed') {
    const formData = new FormData();
    formData.append('doc_id_1', docId1);
    formData.append('doc_id_2', docId2);
    formData.append('comparison_type', comparisonType);
    const response = await axios.post(`${API}/document-compare/compare`, formData, {
      headers: {}
    });
    return response.data;
  }

  async compareUploadedDocuments(file1, file2, comparisonType = 'detailed') {
    const formData = new FormData();
    formData.append('file1', file1);
    formData.append('file2', file2);
    formData.append('comparison_type', comparisonType);
    const response = await axios.post(`${API}/document-compare/compare-upload`, formData, {
      headers: {}
    });
    return response.data;
  }

  async getComparisonHistory() {
    const response = await axios.get(`${API}/document-compare/history`, { headers: this.getHeaders() });
    return response.data;
  }

  // Society Members
  async getSocietyMembers(projectId = null) {
    const query = projectId ? `?project_id=${projectId}` : '';
    const response = await axios.get(`${API}/society-members${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getSocietyMember(memberId) {
    const response = await axios.get(`${API}/society-members/member/${memberId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async createSocietyMember(memberData) {
    const response = await axios.post(`${API}/society-members`, memberData, { headers: this.getHeaders() });
    return response.data;
  }

  async updateSocietyMember(memberId, memberData) {
    const response = await axios.put(`${API}/society-members/${memberId}`, memberData, { headers: this.getHeaders() });
    return response.data;
  }

  async updateSocietyMemberStatus(memberId, agreementStatus, vacatingStatus) {
    const response = await axios.put(`${API}/society-members/${memberId}/status`, {
      agreement_status: agreementStatus,
      vacating_status: vacatingStatus
    }, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteSocietyMember(memberId) {
    const response = await axios.delete(`${API}/society-members/${memberId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getSocietyMemberStats(projectId) {
    const response = await axios.get(`${API}/society-members/stats/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async bulkUploadSocietyMembers(members) {
    const response = await axios.post(`${API}/society-members/bulk`, { members }, { headers: this.getHeaders() });
    return response.data;
  }

  async grammarCheckSocietyMessage(text) {
    const response = await axios.post(`${API}/society-members/grammar-check`, { text }, { headers: this.getHeaders() });
    return response.data;
  }

  async generateLetter(data) {
    const response = await axios.post(`${API}/agents/generate-letter`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async replyToLetter(formData) {
    // formData: FormData with file, reply_tone, reply_type, instructions
    const headers = this.getHeaders();
    delete headers['Content-Type']; // let browser set multipart boundary
    const response = await axios.post(`${API}/agents/reply-to-letter`, formData, {
      headers,
      timeout: 120000,
    });
    return response.data;
  }

  async listLetterHistory({ q = '', mode = '', limit = 20, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (mode) params.set('mode', mode);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const response = await axios.get(`${API}/agents/letter-history?${params}`, {
      headers: this.getHeaders(),
    });
    return response.data;
  }

  async getLetterHistoryItem(letterId) {
    const response = await axios.get(`${API}/agents/letter-history/${letterId}`, {
      headers: this.getHeaders(),
    });
    return response.data;
  }

  async deleteLetterHistoryItem(letterId) {
    const response = await axios.delete(`${API}/agents/letter-history/${letterId}`, {
      headers: this.getHeaders(),
    });
    return response.data;
  }

  async regenerateLetterHistoryItem(letterId) {
    const response = await axios.post(
      `${API}/agents/letter-history/${letterId}/regenerate`,
      {},
      { headers: this.getHeaders(), timeout: 120000 }
    );
    return response.data;
  }

  async sendSocietyMessage(data) {
    const response = await axios.post(`${API}/society-members/send-message`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async getActivityLogs(employeeId = null) {
    const query = employeeId ? `?employee_id=${employeeId}` : '';
    const response = await axios.get(`${API}/activity-logs${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getAlerts(status = 'active', alertType = null) {
    const params = new URLSearchParams({ status });
    if (alertType) params.append('alert_type', alertType);
    const response = await axios.get(`${API}/alerts?${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getWorkflowTemplates() {
    const response = await axios.get(`${API}/workflows`, { headers: this.getHeaders() });
    return response.data;
  }

  async createWorkflowTemplate(templateData) {
    const response = await axios.post(`${API}/workflow-templates`, templateData, { headers: this.getHeaders() });
    return response.data;
  }

  async getDefaultWorkflowStages() {
    const response = await axios.get(`${API}/workflow/default-stages`, { headers: this.getHeaders() });
    return response.data;
  }

  async createWorkflowStage(data) {
    const response = await axios.post(`${API}/workflow/stages`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async updateWorkflowStage(stageId, data) {
    const response = await axios.put(`${API}/workflow/stages/${stageId}`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteWorkflowStage(stageId) {
    const response = await axios.delete(`${API}/workflow/stages/${stageId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async addTaskToStage(stageId, data) {
    const response = await axios.post(`${API}/workflow/stages/${stageId}/tasks`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async updateTaskInStage(stageId, taskId, data) {
    const response = await axios.put(`${API}/workflow/stages/${stageId}/tasks/${taskId}`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteTaskFromStage(stageId, taskId) {
    const response = await axios.delete(`${API}/workflow/stages/${stageId}/tasks/${taskId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async generateWorkflowTasks(projectId, workflowId = null) {
    const params = workflowId ? `?workflow_id=${workflowId}` : '';
    const response = await axios.post(`${API}/workflow/generate-tasks/${projectId}${params}`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectWorkflowProgress(projectId) {
    const response = await axios.get(`${API}/workflow/project-progress/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getWhatsAppStatus() {
    const response = await axios.get(`${API}/whatsapp/status`, { headers: this.getHeaders() });
    return response.data;
  }

  async testWhatsApp(phone, message) {
    const response = await axios.post(`${API}/whatsapp/test?phone=${encodeURIComponent(phone)}&message=${encodeURIComponent(message)}`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async sendWhatsAppReminders() {
    const response = await axios.post(`${API}/whatsapp/send-reminders`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async sendWhatsAppOverdueAlerts() {
    const response = await axios.post(`${API}/whatsapp/send-overdue-alerts`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async sendWhatsAppMorningBriefs() {
    const response = await axios.post(`${API}/whatsapp/send-morning-briefs`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async sendWhatsAppGeofenceSummary() {
    const response = await axios.post(`${API}/whatsapp/send-geofence-summary`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getGeofenceSummaryPreview() {
    const response = await axios.get(`${API}/whatsapp/geofence-summary-preview`, { headers: this.getHeaders() });
    return response.data;
  }

  async getWhatsAppNotificationSettings() {
    const response = await axios.get(`${API}/whatsapp/notification-settings`, { headers: this.getHeaders() });
    return response.data;
  }

  async createWhatsAppNotificationSetting(data) {
    const response = await axios.post(`${API}/whatsapp/notification-settings`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async updateWhatsAppNotificationSetting(key, data) {
    const response = await axios.put(`${API}/whatsapp/notification-settings`, { key, ...data }, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteWhatsAppNotificationSetting(key) {
    const response = await axios.delete(`${API}/whatsapp/notification-settings/${key}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getWhatsAppRunnableKeys() {
    const response = await axios.get(`${API}/whatsapp/notification-settings/run-now/available-keys`, { headers: this.getHeaders() });
    return response.data;
  }

  async runWhatsAppNotificationNow(key) {
    const response = await axios.post(`${API}/whatsapp/notification-settings/${key}/run-now`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getWhatsAppDeliveryLogs(limit = 50, notificationType = null) {
    let url = `${API}/whatsapp/delivery-logs?limit=${limit}`;
    if (notificationType) url += `&notification_type=${notificationType}`;
    const response = await axios.get(url, { headers: this.getHeaders() });
    return response.data;
  }

  async getWhatsAppDeliveryStats() {
    const response = await axios.get(`${API}/whatsapp/delivery-stats`, { headers: this.getHeaders() });
    return response.data;
  }

  async updateEmployeeWhatsApp(employeeId, phone) {
    const response = await axios.put(`${API}/employees/${employeeId}/whatsapp?phone=${phone}`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  // Departments
  async getDepartments() {
    const response = await axios.get(`${API}/departments`, { headers: this.getHeaders() });
    return response.data;
  }

  async createDepartment(departmentData) {
    const response = await axios.post(`${API}/departments`, departmentData, { headers: this.getHeaders() });
    return response.data;
  }

  async updateDepartment(departmentId, departmentData) {
    const response = await axios.put(`${API}/departments/${departmentId}`, departmentData, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteDepartment(departmentId) {
    const response = await axios.delete(`${API}/departments/${departmentId}`, { headers: this.getHeaders() });
    return response.data;
  }

  // Roles
  async getRoles(departmentId = null) {
    const query = departmentId ? `?department_id=${departmentId}` : '';
    const response = await axios.get(`${API}/roles${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async createRole(roleData) {
    const response = await axios.post(`${API}/roles`, roleData, { headers: this.getHeaders() });
    return response.data;
  }

  async updateRole(roleId, roleData) {
    const response = await axios.put(`${API}/roles/${roleId}`, roleData, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteRole(roleId) {
    const response = await axios.delete(`${API}/roles/${roleId}`, { headers: this.getHeaders() });
    return response.data;
  }

  // Project Team
  async addTeamMember(teamData) {
    const response = await axios.post(`${API}/project-team`, teamData, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectTeam(projectId) {
    const response = await axios.get(`${API}/project-team/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getEmployeeProjects(employeeId) {
    const response = await axios.get(`${API}/project-team/employee/${employeeId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async updateTeamMemberRole(id, role) {
    const response = await axios.put(`${API}/project-team/${id}?role=${role}`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async removeTeamMember(id) {
    const response = await axios.delete(`${API}/project-team/${id}`, { headers: this.getHeaders() });
    return response.data;
  }

  // Alerts
  async dismissAlert(alertId) {
    const response = await axios.put(`${API}/alerts/${alertId}/dismiss`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async resolveAlert(alertId) {
    const response = await axios.put(`${API}/alerts/${alertId}/resolve`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async generateAlerts() {
    const response = await axios.post(`${API}/alerts/generate`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async cleanupOrphanAlerts() {
    const response = await axios.post(`${API}/alerts/cleanup-orphans`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getAlertCount() {
    const response = await axios.get(`${API}/alerts/count`, { headers: this.getHeaders() });
    return response.data;
  }

  // Reports
  async generateReport(reportData) {
    const response = await axios.post(`${API}/reports/generate`, reportData, { headers: this.getHeaders() });
    return response.data;
  }

  async getReports(reportType = null, projectId = null) {
    const params = new URLSearchParams();
    if (reportType) params.append('report_type', reportType);
    if (projectId) params.append('project_id', projectId);
    const response = await axios.get(`${API}/reports?${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getReport(reportId) {
    const response = await axios.get(`${API}/reports/${reportId}`, { headers: this.getHeaders() });
    return response.data;
  }

  // Project Health
  async calculateProjectHealth(projectId) {
    const response = await axios.post(`${API}/agents/project-health/${projectId}/calculate`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectHealth(projectId) {
    const response = await axios.get(`${API}/agents/project-health/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getAllProjectHealth() {
    const response = await axios.get(`${API}/agents/project-health`, { headers: this.getHeaders() });
    return response.data;
  }

  // AI Insights
  async generateAIInsights(projectId) {
    const response = await axios.post(`${API}/ai-insights/generate/${projectId}`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getAIInsights(projectId = null, insightType = null, unreadOnly = false) {
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);
    if (insightType) params.append('insight_type', insightType);
    if (unreadOnly) params.append('unread_only', 'true');
    const response = await axios.get(`${API}/ai-insights?${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  async markInsightRead(insightId) {
    const response = await axios.put(`${API}/ai-insights/${insightId}/read`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getInsightsSummary() {
    const response = await axios.get(`${API}/ai-insights/summary`, { headers: this.getHeaders() });
    return response.data;
  }

  // AI Agents
  async getDailyPriorities() {
    const response = await axios.get(`${API}/agents/task/daily-priorities`, { headers: this.getHeaders() });
    return response.data;
  }

  async getTeamWorkload() {
    const response = await axios.get(`${API}/agents/task/team-workload`, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectAgentOverview(projectId) {
    const response = await axios.get(`${API}/agents/project/${projectId}/overview`, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectsSummary() {
    const response = await axios.get(`${API}/agents/project/summary`, { headers: this.getHeaders() });
    return response.data;
  }

  async getMorningBrief() {
    const response = await axios.get(`${API}/agents/brief/morning`, { headers: this.getHeaders() });
    return response.data;
  }

  async getDashboardInsights() {
    const response = await axios.get(`${API}/agents/dashboard-insights`, { headers: this.getHeaders() });
    return response.data;
  }

  // Project Agent - Enhanced
  async getProjectDelayRisks() {
    const response = await axios.get(`${API}/agents/project/delay-risks`, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectStageMonitoring(projectId) {
    const response = await axios.get(`${API}/agents/project/${projectId}/stages`, { headers: this.getHeaders() });
    return response.data;
  }

  // Report Agent
  async getDailyReport() {
    const response = await axios.get(`${API}/agents/report/daily`, { headers: this.getHeaders() });
    return response.data;
  }

  async getWeeklyReport() {
    const response = await axios.get(`${API}/agents/report/weekly`, { headers: this.getHeaders() });
    return response.data;
  }

  // Productivity Agent
  async getWorkloadBalance() {
    const response = await axios.get(`${API}/agents/productivity/workload-balance`, { headers: this.getHeaders() });
    return response.data;
  }

  async getActivityPatterns() {
    const response = await axios.get(`${API}/agents/productivity/activity-patterns`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== NOTIFICATIONS ==========

  async getNotifications(unreadOnly = false, limit = 50) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (unreadOnly) params.append('unread_only', 'true');
    const response = await axios.get(`${API}/notifications?${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getNotificationCount() {
    const response = await axios.get(`${API}/notifications/count`, { headers: this.getHeaders() });
    return response.data;
  }

  async createNotification(notificationData) {
    const response = await axios.post(`${API}/notifications`, notificationData, { headers: this.getHeaders() });
    return response.data;
  }

  async sendBulkNotifications(employeeIds, title, message, notificationType = 'system', channels = ['in_app']) {
    const response = await axios.post(`${API}/notifications/bulk`, {
      employee_ids: employeeIds,
      title,
      message,
      notification_type: notificationType,
      channels
    }, { headers: this.getHeaders() });
    return response.data;
  }

  async markNotificationRead(notificationId) {
    const response = await axios.put(`${API}/notifications/${notificationId}/read`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async markAllNotificationsRead() {
    const response = await axios.put(`${API}/notifications/read-all`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async dismissNotification(notificationId) {
    const response = await axios.put(`${API}/notifications/${notificationId}/dismiss`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteNotification(notificationId) {
    const response = await axios.delete(`${API}/notifications/${notificationId}`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== COMMAND CENTRE ==========

  async getCommandCentreStats() {
    const response = await axios.get(`${API}/command-centre/stats`, { headers: this.getHeaders() });
    return response.data;
  }

  async getCommandCentreProjects() {
    const response = await axios.get(`${API}/command-centre/projects`, { headers: this.getHeaders() });
    return response.data;
  }

  async getDepartmentWorkload() {
    const response = await axios.get(`${API}/command-centre/department-workload`, { headers: this.getHeaders() });
    return response.data;
  }

  async getRecentActivity(limit = 20) {
    const response = await axios.get(`${API}/command-centre/recent-activity?limit=${limit}`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== DESKTOP ACTIVITY ==========

  async getEmployeeDesktopActivity(employeeId, days = 7) {
    const response = await axios.get(`${API}/desktop-activity/employee/${employeeId}?days=${days}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getTeamDesktopActivity(days = 7) {
    const response = await axios.get(`${API}/desktop-activity/team?days=${days}`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== ANALYTICS ENGINE ==========

  async getAnalyticsStatus() {
    const response = await axios.get(`${API}/analytics/status`, { headers: this.getHeaders() });
    return response.data;
  }

  async runAnalyticsJob(jobName) {
    const response = await axios.post(`${API}/analytics/run/${jobName}`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getAnalyticsLogs(jobName = null, limit = 20) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (jobName) params.append('job_name', jobName);
    const response = await axios.get(`${API}/analytics/logs?${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getBottlenecks() {
    const response = await axios.get(`${API}/analytics/bottlenecks`, { headers: this.getHeaders() });
    return response.data;
  }

  async getWeeklyAnalyticsReports(limit = 4) {
    const response = await axios.get(`${API}/analytics/reports/weekly?limit=${limit}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getStoredMorningBrief(employeeId) {
    const response = await axios.get(`${API}/analytics/morning-brief/${employeeId}`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== DAILY OPERATIONS ==========

  async getOperationsSummary() {
    const response = await axios.get(`${API}/operations/summary`, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectsWithStages() {
    const response = await axios.get(`${API}/operations/projects-with-stages`, { headers: this.getHeaders() });
    return response.data;
  }

  async getEmployeeActivityMonitor() {
    const response = await axios.get(`${API}/operations/employee-activity`, { headers: this.getHeaders() });
    return response.data;
  }

  async getTaskDeadlineMonitor(daysAhead = 7) {
    const response = await axios.get(`${API}/operations/task-deadlines?days_ahead=${daysAhead}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getUpcomingMilestones(daysAhead = 30) {
    const response = await axios.get(`${API}/operations/milestones?days_ahead=${daysAhead}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getCriticalAlerts() {
    const response = await axios.get(`${API}/operations/critical-alerts`, { headers: this.getHeaders() });
    return response.data;
  }

  async getOperationsAIInsights() {
    const response = await axios.get(`${API}/operations/ai-insights`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== NEW OPERATIONS MODULES (Points 3-10) ==========

  async getMemberDocumentationTracker() {
    const response = await axios.get(`${API}/operations/member-documentation-tracker`, { headers: this.getHeaders() });
    return response.data;
  }

  async getApprovalStatusTracker() {
    const response = await axios.get(`${API}/operations/approval-status-tracker`, { headers: this.getHeaders() });
    return response.data;
  }

  async getSiteProgressSnapshot() {
    const response = await axios.get(`${API}/operations/site-progress-snapshot`, { headers: this.getHeaders() });
    return response.data;
  }

  async getWorkloadImbalance() {
    const response = await axios.get(`${API}/operations/workload-imbalance`, { headers: this.getHeaders() });
    return response.data;
  }

  async getDelayedTaskImpact() {
    const response = await axios.get(`${API}/operations/delayed-task-impact`, { headers: this.getHeaders() });
    return response.data;
  }

  async getAISuggestedActions() {
    const response = await axios.get(`${API}/operations/ai-suggested-actions`, { headers: this.getHeaders() });
    return response.data;
  }

  async getCrossProjectTimeline(daysAhead = 30) {
    const response = await axios.get(`${API}/operations/cross-project-timeline?days_ahead=${daysAhead}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getDataQualityMonitor() {
    const response = await axios.get(`${API}/operations/data-quality-monitor`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== MESSAGING ==========

  async sendMessage(messageData) {
    const response = await axios.post(`${API}/messages`, messageData, { headers: this.getHeaders() });
    return response.data;
  }

  async sendMessageWithFile(formData) {
    const headers = this.getHeaders();
    delete headers['Content-Type']; // Let browser set multipart boundary
    const response = await axios.post(`${API}/messages/upload-file`, formData, { headers });
    return response.data;
  }

  async getInbox() {
    const response = await axios.get(`${API}/messages/inbox`, { headers: this.getHeaders() });
    return response.data;
  }

  // Alias for dashboard compatibility
  async getInboxMessages() {
    return this.getInbox();
  }

  async getSentMessages() {
    const response = await axios.get(`${API}/messages/sent`, { headers: this.getHeaders() });
    return response.data;
  }

  async getConversation(employeeId) {
    const response = await axios.get(`${API}/messages/conversation/${employeeId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectMessages(projectId) {
    const response = await axios.get(`${API}/messages/project/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async markMessageRead(messageId) {
    const response = await axios.put(`${API}/messages/${messageId}/read`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getUnreadMessageCount() {
    const response = await axios.get(`${API}/messages/unread-count`, { headers: this.getHeaders() });
    return response.data;
  }

  async getConversationsList(currentUserId = null) {
    // Get unique conversations from inbox
    const inbox = await this.getInbox();
    const sent = await this.getSentMessages();

    // Combine and get unique contacts
    const contactMap = new Map();

    [...inbox, ...sent].forEach(msg => {
      const contactId = msg.sender_id === currentUserId
        ? msg.receiver_id
        : msg.sender_id;
      const contactName = msg.sender_id === currentUserId
        ? msg.receiver_name
        : msg.sender_name;

      if (contactId && !contactMap.has(contactId)) {
        contactMap.set(contactId, {
          employee_id: contactId,
          name: contactName,
          last_message: msg.message_text,
          timestamp: msg.timestamp,
          is_read: msg.is_read
        });
      }
    });

    return Array.from(contactMap.values()).sort((a, b) =>
      new Date(b.timestamp) - new Date(a.timestamp)
    );
  }

  // ========== TASK LIBRARY ==========

  async getTaskLibrarySummary() {
    const response = await axios.get(`${API}/task-library/summary`, { headers: this.getHeaders() });
    return response.data;
  }

  async getTaskLibraryStages() {
    const response = await axios.get(`${API}/task-library/stages`, { headers: this.getHeaders() });
    return response.data;
  }

  async getTaskLibraryStage(stageId) {
    const response = await axios.get(`${API}/task-library/stages/${stageId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getTaskLibraryTasks(stageId = null, department = null) {
    const params = new URLSearchParams();
    if (stageId) params.append('stage_id', stageId);
    if (department) params.append('department', department);
    const query = params.toString() ? `?${params}` : '';
    const response = await axios.get(`${API}/task-library/tasks${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getTaskLibraryTask(taskCode) {
    const response = await axios.get(`${API}/task-library/tasks/${taskCode}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getTaskLibraryDepartments() {
    const response = await axios.get(`${API}/task-library/departments`, { headers: this.getHeaders() });
    return response.data;
  }

  async generateProjectTasksFromLibrary(projectId, startDate = null) {
    const params = startDate ? `?start_date=${startDate}` : '';
    const response = await axios.post(`${API}/task-library/generate-project-tasks/${projectId}${params}`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectTaskProgress(projectId) {
    const response = await axios.get(`${API}/task-library/project/${projectId}/progress`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== SYSTEM HEALTH (Admin Only) ==========

  async getCodeHealthOverview() {
    const response = await axios.get(`${API}/system-health/overview`, { headers: this.getHeaders() });
    return response.data;
  }

  async getRouterStats() {
    const response = await axios.get(`${API}/system-health/routers`, { headers: this.getHeaders() });
    return response.data;
  }

  async getDatabaseStats() {
    const response = await axios.get(`${API}/system-health/database`, { headers: this.getHeaders() });
    return response.data;
  }

  async getSystemMetrics() {
    const response = await axios.get(`${API}/system-health/system`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== EMPLOYEE BULK UPLOAD ==========

  async bulkUploadEmployees(file, updateExisting = false) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(
      `${API}/employees/bulk-upload?update_existing=${updateExisting}`,
      formData
    );
    return response.data;
  }

  async getEmployeeCsvTemplate() {
    const response = await axios.get(`${API}/employees/csv-template`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== AI CHAT (NLQ Interface) ==========

  async sendAIChatQuery(message, conversationId = null) {
    const response = await axios.post(`${API}/ai-chat/query`, {
      message,
      conversation_id: conversationId
    }, { headers: this.getHeaders() });
    return response.data;
  }

  async getAIChatConversations() {
    const response = await axios.get(`${API}/ai-chat/conversations`, { headers: this.getHeaders() });
    return response.data;
  }

  async getAIChatConversation(conversationId) {
    const response = await axios.get(`${API}/ai-chat/conversation/${conversationId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteAIChatConversation(conversationId) {
    const response = await axios.delete(`${API}/ai-chat/conversation/${conversationId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async newAIChatConversation() {
    const response = await axios.post(`${API}/ai-chat/new-conversation`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== MS TEAMS WEBHOOK ==========

  async getTeamsWebhookConfig() {
    const response = await axios.get(`${API}/teams-webhook/config`, { headers: this.getHeaders() });
    return response.data;
  }

  async updateTeamsWebhookConfig(config) {
    const response = await axios.put(`${API}/teams-webhook/config`, config, { headers: this.getHeaders() });
    return response.data;
  }

  async testTeamsWebhook(data) {
    const response = await axios.post(`${API}/teams-webhook/test`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async sendTeamsDailySummary() {
    const response = await axios.post(`${API}/teams-webhook/send-summary`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async getTeamsWebhookStatus() {
    const response = await axios.get(`${API}/teams-webhook/status`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== BUDGET MANAGEMENT ==========

  async getBudgets() {
    const response = await axios.get(`${API}/budgets`, { headers: this.getHeaders() });
    return response.data;
  }

  async getBudgetSummary() {
    const response = await axios.get(`${API}/budgets/summary`, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectBudget(projectId) {
    const response = await axios.get(`${API}/budgets/project/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async createBudget(budgetData) {
    const response = await axios.post(`${API}/budgets`, budgetData, { headers: this.getHeaders() });
    return response.data;
  }

  async updateBudget(budgetId, updateData) {
    const response = await axios.put(`${API}/budgets/${budgetId}`, updateData, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectExpenses(projectId) {
    const response = await axios.get(`${API}/budgets/expenses/project/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async createExpense(expenseData) {
    const response = await axios.post(`${API}/budgets/expenses`, expenseData, { headers: this.getHeaders() });
    return response.data;
  }

  async updateExpense(expenseId, updateData) {
    const response = await axios.put(`${API}/budgets/expenses/${expenseId}`, updateData, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteExpense(expenseId) {
    const response = await axios.delete(`${API}/budgets/expenses/${expenseId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getDefaultBudgetCategories() {
    const response = await axios.get(`${API}/budgets/categories/default`, { headers: this.getHeaders() });
    return response.data;
  }

  async uploadBudgetExcel(projectId, file, replaceExisting = false) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${API}/budgets/${projectId}/upload-excel?replace_existing=${replaceExisting}`,
      formData
    );
    return response.data;
  }

  async getBudgetItems(projectId, category = null) {
    const params = category ? { category } : {};
    const response = await axios.get(`${API}/budgets/${projectId}/items`, {
      headers: this.getHeaders(),
      params
    });
    return response.data;
  }

  async getBudgetItemsByCategory(projectId) {
    const response = await axios.get(`${API}/budgets/${projectId}/items/by-category`, { headers: this.getHeaders() });
    return response.data;
  }

  async getBudgetAnalytics() {
    const response = await axios.get(`${API}/budgets/analytics/overview`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== DAILY REPORTS ==========

  async getTodayReport() {
    const response = await axios.get(`${API}/daily-reports/today`, { headers: this.getHeaders() });
    return response.data;
  }

  async submitDailyReport(data) {
    const response = await axios.post(`${API}/daily-reports/`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async getMyReports(days = 30) {
    const response = await axios.get(`${API}/daily-reports/my-reports?days=${days}`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== ADMIN IMPERSONATION ==========

  async impersonateEmployee(employeeId) {
    const response = await axios.post(`${API}/auth/impersonate/${employeeId}`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async transferLogin(fromEmployeeId, toEmployeeId) {
    const response = await axios.post(`${API}/auth/transfer-login`, null, {
      headers: this.getHeaders(),
      params: { from_employee_id: fromEmployeeId, to_employee_id: toEmployeeId }
    });
    return response.data;
  }

  // ========== CLOUD STORAGE (Dropbox & Google Drive) ==========

  async getCloudStorageStatus() {
    const response = await axios.get(`${API}/cloud-storage/status`, { headers: this.getHeaders() });
    return response.data;
  }

  async connectDropbox() {
    const response = await axios.get(`${API}/cloud-storage/dropbox/connect`, { headers: this.getHeaders() });
    return response.data;
  }

  async connectGoogleDrive() {
    const response = await axios.get(`${API}/cloud-storage/google-drive/connect`, { headers: this.getHeaders() });
    return response.data;
  }

  async disconnectCloudStorage(provider) {
    const response = await axios.delete(`${API}/cloud-storage/disconnect/${provider}`, { headers: this.getHeaders() });
    return response.data;
  }

  async listDropboxFiles(path = '') {
    const response = await axios.get(`${API}/cloud-storage/dropbox/files`, {
      headers: this.getHeaders(),
      params: { path }
    });
    return response.data;
  }

  async listGoogleDriveFiles(folderId = 'root') {
    const response = await axios.get(`${API}/cloud-storage/google-drive/files`, {
      headers: this.getHeaders(),
      params: { folder_id: folderId }
    });
    return response.data;
  }

  async getDropboxDownloadLink(filePath) {
    const response = await axios.get(`${API}/cloud-storage/dropbox/download-link`, {
      headers: this.getHeaders(),
      params: { file_path: filePath }
    });
    return response.data;
  }

  async getGoogleDriveDownloadLink(fileId) {
    const response = await axios.get(`${API}/cloud-storage/google-drive/download-link`, {
      headers: this.getHeaders(),
      params: { file_id: fileId }
    });
    return response.data;
  }

  async downloadCloudFileToApp(fileData) {
    const response = await axios.post(`${API}/cloud-storage/download-to-app`, fileData, { headers: this.getHeaders() });
    return response.data;
  }
  // ========== SEARCH ==========

  async universalSearch(query) {
    const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== DECISIONS ==========

  async getDecisionRequests(projectId, status, view = 'all') {
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);
    if (status) params.append('status', status);
    params.append('view', view);
    const response = await axios.get(`${API}/decisions/requests?${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  async createDecisionRequest(data) {
    const response = await axios.post(`${API}/decisions/requests`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async actionDecisionRequest(requestId, action) {
    const response = await axios.post(`${API}/decisions/requests/${requestId}/action`, action, { headers: this.getHeaders() });
    return response.data;
  }

  async getDecisionStats() {
    const response = await axios.get(`${API}/decisions/stats`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== BACKUP ==========

  async getBackupStatus() {
    const response = await axios.get(`${API}/backup/status`, { headers: this.getHeaders() });
    return response.data;
  }

  async triggerDbBackup() {
    const response = await axios.post(`${API}/backup/database`, {}, { headers: this.getHeaders(), timeout: 300000 });
    return response.data;
  }

  async getBackupFiles(prefix = '', limit = 50) {
    const response = await axios.get(`${API}/backup/files?prefix=${prefix}&limit=${limit}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getBackupLogs(limit = 20) {
    const response = await axios.get(`${API}/backup/logs?limit=${limit}`, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteBackupFile(fileId, fileName) {
    const response = await axios.delete(`${API}/backup/files/${fileId}?file_name=${encodeURIComponent(fileName)}`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== CASH FLOW ==========

  async getCashflowEntries(projectId = null, month = null, phase = null, entryType = null) {
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);
    if (month) params.append('month', month);
    if (phase) params.append('phase', phase);
    if (entryType) params.append('entry_type', entryType);
    const query = params.toString() ? `?${params}` : '';
    const response = await axios.get(`${API}/cashflow/entries${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async createCashflowEntry(data) {
    const response = await axios.post(`${API}/cashflow/entries`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async updateCashflowEntry(entryId, data) {
    const response = await axios.put(`${API}/cashflow/entries/${entryId}`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteCashflowEntry(entryId) {
    const response = await axios.delete(`${API}/cashflow/entries/${entryId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getCashflowSummary(projectId = null) {
    const query = projectId ? `?project_id=${projectId}` : '';
    const response = await axios.get(`${API}/cashflow/summary${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getCashflowPhaseSummary(projectId = null) {
    const query = projectId ? `?project_id=${projectId}` : '';
    const response = await axios.get(`${API}/cashflow/phase-summary${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getCashflowCategorySummary(projectId = null, entryType = null) {
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);
    if (entryType) params.append('entry_type', entryType);
    const query = params.toString() ? `?${params}` : '';
    const response = await axios.get(`${API}/cashflow/category-summary${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getCashflowStatement(projectId = null) {
    const query = projectId ? `?project_id=${projectId}` : '';
    const response = await axios.get(`${API}/cashflow/cash-flow-statement${query}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getCashflowCategories() {
    const response = await axios.get(`${API}/cashflow/categories`, { headers: this.getHeaders() });
    return response.data;
  }

  async getCompanyEntities() {
    const response = await axios.get(`${API}/cashflow/entities`, { headers: this.getHeaders() });
    return response.data;
  }

  async createCompanyEntity(data) {
    const response = await axios.post(`${API}/cashflow/entities`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async mapProjectToEntity(projectId, entityId) {
    const response = await axios.post(`${API}/cashflow/entities/map-project`, { project_id: projectId, entity_id: entityId }, { headers: this.getHeaders() });
    return response.data;
  }

  async unmapProjectFromEntity(projectId, entityId) {
    const response = await axios.delete(`${API}/cashflow/entities/unmap-project?project_id=${projectId}&entity_id=${entityId}`, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== PRODUCTIVITY SCORE ==========

  async getMyProductivityScore(periodType = 'daily') {
    const response = await axios.get(`${API}/productivity-score/my-score?period_type=${periodType}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getMyAllProductivityScores() {
    const response = await axios.get(`${API}/productivity-score/my-scores`, { headers: this.getHeaders() });
    return response.data;
  }

  async getTeamProductivityScores() {
    const response = await axios.get(`${API}/productivity-score/team-scores`, { headers: this.getHeaders() });
    return response.data;
  }

  async getProductivityScoreHistory(periodType = 'daily', limit = 30) {
    const response = await axios.get(`${API}/productivity-score/history?period_type=${periodType}&limit=${limit}`, { headers: this.getHeaders() });
    return response.data;
  }

  async recalculateProductivityScore(periodType = 'daily') {
    const response = await axios.post(`${API}/productivity-score/recalculate?period_type=${periodType}`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  // ========== FINANCE REMINDERS ==========

  async getPendingFinanceReminders(reminderType = null, days = 30) {
    const params = new URLSearchParams({ days: days.toString() });
    if (reminderType) params.append('reminder_type', reminderType);
    const response = await axios.get(`${API}/finance-reminders/pending?${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getFinanceReminderHistory(reminderType = null, status = null, limit = 100) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (reminderType) params.append('reminder_type', reminderType);
    if (status) params.append('status', status);
    const response = await axios.get(`${API}/finance-reminders/history?${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getFinanceReminderStats() {
    const response = await axios.get(`${API}/finance-reminders/stats`, { headers: this.getHeaders() });
    return response.data;
  }

  async triggerFinanceReminders() {
    const response = await axios.post(`${API}/finance-reminders/send-now`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async scheduleRentReminders() {
    const response = await axios.post(`${API}/finance-reminders/schedule-rent`, {}, { headers: this.getHeaders() });
    return response.data;
  }

  async cancelFinanceReminder(reminderId) {
    const response = await axios.delete(`${API}/finance-reminders/${reminderId}`, { headers: this.getHeaders() });
    return response.data;
  }

  // ── New Purchasers ──
  async getNewPurchasers(status) {
    const params = status ? `?status=${status}` : '';
    const response = await axios.get(`${API}/new-purchasers${params}`, { headers: this.getHeaders() });
    return response.data;
  }

  async getNewPurchaserStats() {
    const response = await axios.get(`${API}/new-purchasers/stats`, { headers: this.getHeaders() });
    return response.data;
  }

  async createNewPurchaser(data) {
    const response = await axios.post(`${API}/new-purchasers`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async updateNewPurchaser(id, data) {
    const response = await axios.put(`${API}/new-purchasers/${id}`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async deleteNewPurchaser(id) {
    const response = await axios.delete(`${API}/new-purchasers/${id}`, { headers: this.getHeaders() });
    return response.data;
  }

  async addPurchaserInteraction(id, data) {
    const response = await axios.post(`${API}/new-purchasers/${id}/interaction`, data, { headers: this.getHeaders() });
    return response.data;
  }

  // RERA
  async getRERADashboard() {
    const response = await axios.get(`${API}/rera/dashboard`, { headers: this.getHeaders() });
    return response.data;
  }

  async getRERAReminders(projectId) {
    const url = projectId ? `${API}/rera/reminders?project_id=${projectId}` : `${API}/rera/reminders`;
    const response = await axios.get(url, { headers: this.getHeaders() });
    return response.data;
  }

  async updateProjectRERA(projectId, data) {
    const response = await axios.put(`${API}/rera/project/${projectId}`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async getProjectDocumentFolders(projectId) {
    const response = await axios.get(`${API}/rera/document-folders/${projectId}`, { headers: this.getHeaders() });
    return response.data;
  }

  async post(endpoint, data) {
    const response = await axios.post(`${API}${endpoint}`, data, { headers: this.getHeaders() });
    return response.data;
  }

  async get(endpoint) {
    const response = await axios.get(`${API}${endpoint}`, { headers: this.getHeaders() });
    return response.data;
  }
}

const apiClient = new ApiClient();

// ── Token access surface (Phase B, Feb 2026) ─────────────────────────────
// • The JWT is stored ONLY in the module-scope `_inMemoryToken` variable
//   declared near the top of this file. XSS payloads can no longer read
//   it out of sessionStorage/localStorage because it isn't there.
// • The HttpOnly `auth_token` cookie remains the source of truth for
//   authentication across reloads. It's attached automatically by the
//   browser on every same-site fetch/axios request (see
//   `axios.defaults.withCredentials = true` above) and is NOT readable
//   from JavaScript, so XSS cannot steal it either.
// • `getToken()` returns null after a hard reload until `rehydrateToken()`
//   runs (App.js does this in its boot effect). All same-origin requests
//   still authenticate via cookie during that brief window.
// • `rehydrateToken()` calls POST /api/auth/refresh which uses the cookie
//   to mint a fresh access token, populating the in-memory store so that
//   Bearer-fallback / XHR uploads keep working.
export function getToken() {
  return _inMemoryToken;
}

export function setToken(token) {
  _inMemoryToken = token || null;
  _isAuthenticated = !!token;
}

export function isAuthenticated() {
  return _isAuthenticated;
}

export async function rehydrateToken() {
  try {
    const response = await axios.post(`${API}/auth/refresh`, {});
    const fresh = response?.data?.token;
    if (fresh) {
      _inMemoryToken = fresh;
      _isAuthenticated = true;
      return true;
    }
  } catch {
    // 401 = cookie missing or expired → user must log in again.
  }
  _inMemoryToken = null;
  _isAuthenticated = false;
  return false;
}

export function authHeaders() {
  // No Authorization header needed — auth is via HTTP-only cookie (sent automatically)
  return {};
}

export function authJsonHeaders() {
  return { 'Content-Type': 'application/json' };
}

// ───────────────────────────────────────────────────────────────────────────
// apiFetch — shared helper for all form/CRUD requests across the app.
//
// Why this exists (RCA from May 2026 RERA + CompetitorTracker bug fixes):
//   1. React forms naturally hold "" for unfilled optional numeric fields.
//      Pydantic's `Optional[int]` / `Optional[float]` reject "" with a 422 →
//      backend rejection that looks like "the form silently failed".
//   2. FastAPI 422 returns `detail` as an array of error objects. Code that
//      did `toast.error(e.detail)` rendered objects as React children →
//      uncaught runtime error → dialog vanished → user thought "nothing
//      happens".
//
// What apiFetch does:
//   • Auto-converts "" → null in JSON request bodies (recursive). Frontend
//     forms can keep using empty strings as their idle state and Pydantic
//     Optional fields will accept null.
//   • Always returns parsed JSON on success (or null for 204).
//   • On non-2xx: throws `ApiError` whose `.message` is ALWAYS a safe string
//     (Pydantic detail array → joined `msg`s; object detail → JSON; string →
//     verbatim). `.status` and the raw `.detail` are also preserved.
//   • Inherits the global Authorization + credentials behaviour from the
//     overridden `window.fetch` above. FormData is passed through untouched
//     so the browser keeps its multipart boundary.
//
// Usage:
//   import { apiFetch, ApiError } from '../utils/api';
//   try {
//     const data = await apiFetch('/api/competitors/', { method: 'POST', body: form });
//     toast.success('Saved');
//   } catch (e) {
//     toast.error(e.message);  // Always a string, safe to render
//   }
// ───────────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(message, status, detail) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

function sanitizeJsonBody(obj) {
  if (Array.isArray(obj)) return obj.map(sanitizeJsonBody);
  if (obj === null || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '') out[k] = null;
    else if (Array.isArray(v)) out[k] = v.map(sanitizeJsonBody);
    else if (v !== null && typeof v === 'object' && !(v instanceof Date)) out[k] = sanitizeJsonBody(v);
    else out[k] = v;
  }
  return out;
}

function formatErrorDetail(detail, fallback) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => (d && typeof d === 'object' && d.msg) ? d.msg : JSON.stringify(d)).join(', ');
  }
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message;
    return JSON.stringify(detail);
  }
  return fallback;
}

/**
 * formatApiSuccess(data, fallback?) — extracts a user-facing success message
 * from a parsed API response. Most backend handlers return `{ message: "..." }`
 * or `{ status: "ok", message: "..." }` or just data. Returns the fallback
 * if no message field is found. Always returns a string.
 *
 * Usage:
 *   const data = await apiFetch('/api/foo', { method: 'POST', body });
 *   toast.success(formatApiSuccess(data, 'Saved'));
 */
export function formatApiSuccess(data, fallback = 'Done') {
  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message) return data.message;
    if (typeof data.detail === 'string' && data.detail) return data.detail;
  }
  return fallback;
}

/**
 * formatApiError(err, fallback?) — safely converts ANY error into a string for `toast.error()`.
 * Handles: ApiError (this lib), axios errors (err.response.data.detail),
 *          plain Error (err.message), Pydantic 422 detail arrays, raw strings, undefined.
 * Always returns a string — never an object — so React can render it.
 */
export function formatApiError(err, fallback = 'Something went wrong') {
  if (!err) return fallback;
  if (err instanceof ApiError) return err.message;
  // axios-style error: err.response.data.detail
  const axiosDetail = err?.response?.data?.detail;
  if (axiosDetail !== undefined) return formatErrorDetail(axiosDetail, fallback);
  if (err?.response?.data && typeof err.response.data === 'object') {
    return formatErrorDetail(err.response.data, fallback);
  }
  if (typeof err.message === 'string' && err.message) return err.message;
  if (typeof err === 'string') return err;
  try { return JSON.stringify(err); } catch { return fallback; }
}

export async function apiFetch(path, options = {}) {
  const { method = 'GET', body, headers, sanitize = true, ...rest } = options;

  // Resolve URL: accept absolute URL, '/api/...' path, or bare path.
  let url;
  if (/^https?:\/\//i.test(path)) url = path;
  else if (path.startsWith('/api/')) url = `${BACKEND_URL}${path}`;
  else url = `${API}${path.startsWith('/') ? path : `/${path}`}`;

  // Build payload + headers
  let payload = body;
  const finalHeaders = { ...(headers || {}) };
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const isBlobLike = body && (body instanceof Blob || body instanceof ArrayBuffer);

  if (body !== undefined && body !== null && !isFormData && !isBlobLike && typeof body === 'object') {
    const cleaned = sanitize ? sanitizeJsonBody(body) : body;
    payload = JSON.stringify(cleaned);
    if (!finalHeaders['Content-Type'] && !finalHeaders['content-type']) {
      finalHeaders['Content-Type'] = 'application/json';
    }
  }

  const res = await fetch(url, { method, body: payload, headers: finalHeaders, ...rest });

  if (!res.ok) {
    let detail = null;
    try {
      const text = await res.text();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          detail = parsed.detail !== undefined ? parsed.detail : parsed;
        } catch {
          detail = text;
        }
      }
    } catch (e) {
      // Network read failed (interrupted/aborted). Non-fatal — we still want to
      // throw an ApiError below so callers can react. Log for debugging.
      console.warn('[apiFetch] failed to read error response body:', e);
    }
    const message = formatErrorDetail(detail, `HTTP ${res.status}`);
    throw new ApiError(message, res.status, detail);
  }

  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

export default apiClient;
